/**
 * Debug-Eingriffe als **pure Funktionen** — §60, Zyklus 11d.
 *
 * Dieses Modul ist die Cheat-API, soweit sie Spiellogik ist. Es schreibt nichts, verteilt
 * nichts und kennt keinen Admin: es nimmt einen Zustand und gibt einen neuen zurück, wie jede
 * andere Funktion dieses Pakets (`CLAUDE.md` Regel 4). Die Berechtigung, die Markierung an der
 * Partie (**E39**) und das Ereignis an die Mitspieler liegen eine Schicht höher.
 *
 * **Vier Zusagen, die zusammen gelten — und einzeln nichts wert sind:**
 *
 * | Zusage | woher |
 * |---|---|
 * | Ein Eingriff ist **kein Zug**: keine Zugnummer, keine Zugreihenfolge, keine Bedenkzeit | Nr. 37 |
 * | Der erste schreibende Eingriff **hält die Partie an** | Nr. 37 |
 * | **Konsistenz nie, Regeln nur mit „trotzdem"** | Nr. 38 |
 * | Schreibend nur in **`PLAYING`** und **`PAUSED`** | Nr. 41 |
 *
 * **Warum die Zusagen hier liegen und nicht im Server.** Sie sind Aussagen über den Zustand
 * einer Partie, und der Zustand lebt hier. Läge das Anhalten im Aufrufpfad, müsste jeder
 * künftige Eingriff daran denken — und der zweite oder dritte wäre der vergessene. Hier ist es
 * **eine** Stelle (`intervene`), durch die alle gehen.
 *
 * **Die drei Klassen aus Nr. 38, und wie sie hier aussehen:**
 *
 * - **Klasse A** — unerreichbar, aber regelkonform: gar keine Einwendung. Ein Fähnrich auf das
 *   Zielfeld, ein Wächter acht Felder vor seiner Startzone: beides geht ohne Weiteres.
 * - **Klasse B** — regelwidrig, strukturell sauber: eine Einwendung `kind: 'RULE'`. Abgewiesen,
 *   bis `force` gesetzt ist; danach steht sie in `overridden`, und **daran** erkennt der
 *   Aufrufer, dass er die Partie dauerhaft als „regelwidrig erzeugt" markieren muss.
 * - **Klasse C** — strukturell unmöglich: eine Einwendung `kind: 'CONSISTENCY'`. Von `force`
 *   **nicht** aufhebbar, und das ist der Kern von Nr. 38: hier liegt die Zusage, auf die sich
 *   die ganze Engine stützt (`board-state.ts`: „Diese Funktionen prüfen Konsistenz, nicht
 *   Spielregeln").
 *
 * **Warum Einwendungen Rückgabewerte sind und keine Ausnahmen.** `board-state.ts` wirft bei
 * einer Konsistenzverletzung, weil dort ein Programmierfehler vorliegt: die Regelschicht hat
 * vorher zugesichert, dass der Zug möglich ist. Bei einem Debug-Eingriff gibt es diese
 * Regelschicht davor **nicht** — der Eingriff *ist* die Eingabe, und sie kommt von einem
 * Menschen, der Koordinaten tippt. Ein falsches Feld ist hier also kein Fehler im Code,
 * sondern eine Antwort: „so nicht, und zwar deshalb". Nebenbei fällt damit die Trockenprobe
 * kostenlos heraus — wer wissen will, was ein Eingriff bricht, ruft ihn ohne `force`.
 *
 * **Ein Code, kein Text.** Die Einwendungen tragen stabile Kennungen und keine Meldungen: der
 * Code ist die Schnittstelle, der Satz für Menschen entsteht dort, wo die anderen Sätze
 * entstehen (`apps/server/src/routes/errors.ts`, `apps/web` über i18n — `CLAUDE.md` Regel 6).
 */
import { isOnBoard } from './board.js';
import { capturePiece, capturedOf, discardPiece, movePiece, pieceAt, pieceById, piecesOf, placePiece, respawnPiece, } from './board-state.js';
import { pauseGame } from './game-clock.js';
import { clearPendingAction, isEliminated, scoreOf, setBoardState, setScore, } from './game-state.js';
import { isGoalCell, mayEnterGoalCell } from './goal-cell.js';
import { hexEquals } from './hex.js';
import { pieceCount } from './pieces.js';
import { stopClock } from './timer.js';
/** Klasse C: strukturell unmöglich. Nie übergehbar. */
export const DEBUG_CONSISTENCY_CODES = [
    /** Diese Figur gibt es nicht — weder auf dem Brett noch im Respawn-Pool. */
    'UNKNOWN_PIECE',
    /** Die Koordinate ist auf diesem Brett kein Feld. */
    'CELL_NOT_ON_BOARD',
    /** Dort steht schon eine andere Figur — zwei auf einem Feld gibt es nicht. */
    'CELL_OCCUPIED',
    /** Dieser Spieler spielt in dieser Partie nicht mit (Nr. 38: „ein Sektor ohne Spieler"). */
    'UNKNOWN_OWNER',
    /** Ein Punktestand ist eine nicht negative Zählung — §23 kennt keine halben Punkte. */
    'SCORE_NOT_A_COUNT',
    /**
     * Diese Partie hat **keine Uhr** — Variante A (Nr. 22).
     *
     * Kein Regelverstoß, über den sich streiten ließe, sondern ein Zustand ohne das, was der
     * Eingriff setzen will: `force` hilft hier nicht, weil es nichts zu setzen gibt. Deshalb eine
     * eigene Antwort und nicht Klasse B.
     */
    'NO_CLOCK',
    /** Eine Restzeit ist eine nicht negative Dauer in ganzen Millisekunden. */
    'TIME_NOT_A_DURATION',
];
/** Klasse B: regelwidrig, aber strukturell sauber. Nur mit ausdrücklichem `force`. */
export const DEBUG_RULE_CODES = [
    /** §19: „Nur Fähnriche dürfen dieses Feld betreten." */
    'GOAL_CELL_FLAG_BEARER_ONLY',
    /**
     * Der **Figurensatz** weicht vom RuleConfig-Snapshot ab (Nr. 42).
     *
     * Geprüft wird der Zustand **danach**, nicht die Art des Eingriffs: wer eine entfernte Figur
     * wieder hinzufügt, stellt den Satz wieder her und braucht kein „trotzdem". Die Markierung an
     * der Partie bleibt davon unberührt — sie ist nach **E39** unumkehrbar.
     */
    'PIECE_COUNT_MISMATCH',
    /**
     * Die Restzeit liegt **über** dem konfigurierten Budget (Nr. 43).
     *
     * Herabsetzen ist frei — jeder Wert unter `timeBudgetMs` entsteht im Spiel von selbst.
     * Geprüft wird wie bei Nr. 42 der Zustand **danach**, also ist das Zurücknehmen frei.
     */
    'TIME_OVER_BUDGET',
    /**
     * Die aktive Person ist **ausgeschieden** (Nr. 44).
     *
     * §23 verlangt, sie zu überspringen. Wer ihr den Zug trotzdem gibt, hebt ihr Ausscheiden
     * praktisch auf: der Respawn-Pool bleibt beim Zeitablauf unberührt, und ein Respawn ist eine
     * wählbare Aktion (§22) — die zurückgeholten Figuren stehen auch dann noch da, wenn der Zug
     * längst weitergegeben ist.
     */
    'ELIMINATED_ACTIVE_PLAYER',
];
/** In welchen Phasen ein schreibender Eingriff überhaupt stattfindet (Nr. 41). */
const WRITABLE_STATUSES = ['PLAYING', 'PAUSED'];
/**
 * Der gemeinsame Abschluss jedes Eingriffs: anwenden, Preview verwerfen, anhalten.
 *
 * Die Reihenfolge ist die Zusage: **erst prüfen, dann anwenden, dann anhalten.** Ein
 * abgelehnter Eingriff lässt die Partie laufen — würde sie auch dann anhalten, wäre das
 * Anhalten die einzige Wirkung eines Fehlversuchs, und in `PAUSED` sieht nach Nr. 26 niemand
 * mehr das Brett. Ein Tippfehler würde so eine Partie blind stellen.
 *
 * **Nur Klasse B kommt hier an.** Strukturelle Einwendungen (Klasse C) weist jeder Eingriff
 * schon vorher ab — sie sind nie übergehbar, und der neue Brettzustand ließe sich mit ihnen
 * gar nicht bauen (`placePiece` auf ein besetztes Feld wirft).
 */
function settle(applied, rules, force, now) {
    if (rules.length > 0 && !force) {
        return { ok: false, objections: rules };
    }
    // Die Preview galt für eine Stellung, die es nicht mehr gibt. Sie ist transient (§41) und
    // wird verworfen, statt beim nächsten Auflösen eine illegale Aktion zu beschreiben. Beim
    // Wechsel der aktiven Person ist es mehr als Vorsicht: eine Preview gehört dem Spieler am
    // Zug (§50), und ein Zustand, in dem das nicht gilt, ließe sich nicht mehr laden.
    const cleared = clearPendingAction(applied);
    const next = cleared.status === 'PLAYING' ? pauseGame(cleared, now) : cleared;
    return { ok: true, state: next, overridden: rules.map((rule) => rule.code) };
}
/** Spielt dieser Spieler in dieser Partie mit? */
function knowsPlayer(state, id) {
    return state.players.some((player) => player.id === id);
}
/** Eine Ablehnung, die kein `force` aufhebt. */
function refuse(...codes) {
    return {
        ok: false,
        objections: codes.map((code) => ({ kind: 'CONSISTENCY', code })),
    };
}
/** Wo eine Figur gerade ist — oder `undefined`, wenn es sie nicht gibt. */
function locationOf(board, id) {
    if (pieceById(board, id) !== undefined)
        return 'BOARD';
    return board.captured.has(id) ? 'POOL' : undefined;
}
/** Wie viele Figuren dieser Art dieser Spieler hat — **Brett und Pool zusammen** (Nr. 42). */
function countOfKind(board, owner, type) {
    const onBoard = piecesOf(board, owner).filter((piece) => piece.type === type).length;
    const inPool = capturedOf(board, owner).filter((piece) => piece.type === type).length;
    return onBoard + inPool;
}
/**
 * Weicht der Figurensatz **nach** dem Eingriff vom Snapshot ab? (Nr. 42)
 *
 * Geprüft wird der Zustand danach und nicht die Art des Eingriffs — damit ist das Zurücknehmen
 * frei: wer eine entfernte Figur wieder hinzufügt, stellt den Satz wieder her und braucht kein
 * „trotzdem".
 */
function setMismatch(state, board, owner, type) {
    const soll = pieceCount(state.ruleConfig, type);
    return countOfKind(board, owner, type) === soll
        ? []
        : [{ kind: 'RULE', code: 'PIECE_COUNT_MISMATCH' }];
}
/**
 * Die nächste freie Kennung für eine neue Figur dieses Besitzers und dieser Klasse.
 *
 * Dasselbe Muster wie in der Aufstellung (`setup.ts`, `setupRoster`) und **deterministisch** —
 * die Engine kennt keinen Zufall (Regel 4), und zwei gleiche Partien sollen gleich bleiben
 * (§128). Gesucht wird der kleinste freie Index, nicht der nächste nach dem höchsten: nach
 * einem Entfernen soll die Lücke wieder gefüllt werden.
 */
function freeId(board, owner, type) {
    for (let index = 1;; index += 1) {
        const candidate = `${owner}-${type}-${index}`;
        if (locationOf(board, candidate) === undefined)
            return candidate;
    }
}
/** Die Einwendung aus §19, falls sie greift. */
function goalCellRule(to, type) {
    return isGoalCell(to) && !mayEnterGoalCell(type)
        ? [{ kind: 'RULE', code: 'GOAL_CELL_FLAG_BEARER_ONLY' }]
        : [];
}
/** Was gegen **dieses Zielfeld** spricht, strukturell (Klasse C). */
function cellObjections(context, to, mover) {
    const codes = [];
    if (!isOnBoard(context.board, to))
        codes.push('CELL_NOT_ON_BOARD');
    const occupant = pieceAt(context.state, to);
    if (occupant !== undefined && occupant.id !== mover)
        codes.push('CELL_OCCUPIED');
    return codes;
}
/** Wirft, wenn in dieser Phase nicht geschrieben werden darf (Nr. 41). */
function requireWritable(state) {
    if (!WRITABLE_STATUSES.includes(state.status)) {
        throw new Error(`Ein schreibender Debug-Eingriff gehört in PLAYING oder PAUSED, nicht in ` +
            `${state.status} (docs/OPEN_QUESTIONS.md Nr. 41)`);
    }
}
/**
 * Eine Figur auf ein Feld setzen (§60, „Figuren verschieben") — **vom Brett oder aus dem Pool.**
 *
 * **Keine Prüfung „wäre das erspielbar"** — Nr. 38 schließt sie ausdrücklich aus: die
 * Startzone sind die Reihen A und B, das Zielfeld liegt acht Felder entfernt, und jede
 * interessante Teststellung wäre damit unerreichbar.
 *
 * **Aus dem Pool ist es Klasse A** (Nr. 42): der Figurensatz ändert sich nicht, und regulär ist
 * die Stellung über einen Respawn (§22) plus ein paar Züge erreichbar. Deshalb braucht dieser
 * Weg kein „trotzdem" — und deshalb ist es derselbe Eingriff und nicht ein zweiter.
 *
 * **Der Eingriff auf das eigene Feld ist erlaubt und wirkungslos.** Er gibt denselben Zustand
 * zurück — identisch, nicht nur gleich —, und daran erkennt der Aufrufer, dass nichts zu
 * schreiben, nichts zu markieren und nichts zu verteilen ist. Dieselbe Entscheidung wie in der
 * Aufstellung (Nr. 32), wo ein abgebrochener Zieh-Vorgang im UI kein Regelverstoß ist.
 */
export function debugMovePiece(context, state, input, now) {
    requireWritable(state);
    const board = state.boardState;
    const where = locationOf(board, input.pieceId);
    if (where === undefined) {
        // Ohne Figur ist über das Zielfeld nichts zu sagen — eine zweite Einwendung wäre geraten.
        return refuse('UNKNOWN_PIECE');
    }
    const placed = pieceById(board, input.pieceId);
    if (placed !== undefined && hexEquals(placed.at, input.to)) {
        return { ok: true, state, overridden: [] };
    }
    const blocking = cellObjections(context, input.to, input.pieceId);
    if (blocking.length > 0) {
        return refuse(...blocking);
    }
    const type = placed?.type ?? board.captured.get(input.pieceId)?.type;
    const next = where === 'BOARD'
        ? movePiece(board, input.pieceId, input.to)
        : respawnPiece(board, input.pieceId, input.to);
    // Der Satz ändert sich bei einer Verschiebung nie — geprüft wird nur §19.
    return settle(setBoardState(state, next), type === undefined ? [] : goalCellRule(input.to, type), input.force === true, now);
}
/**
 * Eine Figur **ganz** aus dem Spiel nehmen (§60, „Figuren entfernen") — vom Brett oder aus dem
 * Pool.
 *
 * **Immer Klasse B**, solange der Satz dadurch vom Snapshot abweicht (Nr. 42): im Spiel ist eine
 * Figur nie fort, ein Schlag legt sie in den Pool (§22), auch der Stoß über den Rand (§18).
 * Wer eine Figurenklasse dauerhaft anders besetzen will, ändert `pieces.<klasse>.count` in der
 * RuleConfig — dort steht sie im Snapshot und die Partie bleibt nachrechenbar (Nr. 38).
 */
export function debugRemovePiece(_context, state, input, now) {
    requireWritable(state);
    const board = state.boardState;
    const piece = pieceById(board, input.pieceId) ?? board.captured.get(input.pieceId);
    if (piece === undefined) {
        return refuse('UNKNOWN_PIECE');
    }
    const next = discardPiece(board, input.pieceId);
    return settle(setBoardState(state, next), setMismatch(state, next, piece.owner, piece.type), input.force === true, now);
}
/**
 * Eine **neue** Figur erzeugen und aufs Brett setzen (§60, „Figuren hinzufügen").
 *
 * **Die Kennung vergibt die Engine**, nicht der Aufrufer: sie muss frei sein, und eine von außen
 * gewählte wäre der einzige Weg, zwei Figuren mit derselben ID zu erzeugen — eine
 * Konsistenzverletzung, die kein `force` je erlauben darf (Nr. 38).
 *
 * **Klasse B, wenn der Satz danach abweicht** (Nr. 42) — und ausdrücklich **nicht**, wenn eine
 * zuvor entfernte Figur damit ersetzt wird. Ein unbekannter Besitzer ist dagegen Klasse C: „ein
 * Sektor ohne Spieler" nennt Nr. 38 selbst als Beispiel.
 */
export function debugAddPiece(context, state, input, now) {
    requireWritable(state);
    const board = state.boardState;
    const blocking = [...cellObjections(context, input.to, undefined)];
    if (!knowsPlayer(state, input.owner)) {
        blocking.push('UNKNOWN_OWNER');
    }
    if (blocking.length > 0) {
        return refuse(...blocking);
    }
    const id = freeId(board, input.owner, input.type);
    const next = placePiece(board, {
        id,
        type: input.type,
        owner: input.owner,
        at: input.to,
    });
    return settle(setBoardState(state, next), [...goalCellRule(input.to, input.type), ...setMismatch(state, next, input.owner, input.type)], input.force === true, now);
}
/**
 * Eine Figur vom Brett in den **Respawn-Pool** legen (§60, „Respawn-Pools verändern").
 *
 * **Klasse A** (Nr. 42): genau das tut jeder Schlag (§22), der Figurensatz ändert sich nicht.
 * Der Weg zurück ist `debugMovePiece` — damit ist der Pool in beide Richtungen bedienbar, ohne
 * dass ein Eingriff die Partie markiert.
 *
 * **Eine Figur, die schon im Pool liegt, ändert nichts** und gibt denselben Zustand zurück —
 * dasselbe Muster wie beim Setzen auf das eigene Feld (Nr. 32).
 */
export function debugPoolPiece(_context, state, input, now) {
    requireWritable(state);
    const board = state.boardState;
    const where = locationOf(board, input.pieceId);
    if (where === undefined) {
        return refuse('UNKNOWN_PIECE');
    }
    if (where === 'POOL') {
        return { ok: true, state, overridden: [] };
    }
    return settle(setBoardState(state, capturePiece(board, input.pieceId)), [], input.force === true, now);
}
/**
 * Den Punktestand eines Spielers **setzen** (§60, „Punkte verändern").
 *
 * **Immer Klasse A** (Nr. 39): der Wert wird gesetzt, die Siegprüfung bleibt am Zugbeginn. Eine
 * laufende Partie darf nach diesem Eingriff das Punkteziel schon erfüllen — §26 hängt die
 * Prüfung an einen Zug, und ein Eingriff ist keiner (Nr. 37). Beendet wird sie dann von der
 * nächsten Aktion. Wer einen Zustand liest, muss damit rechnen; `GAME_RULES.md` Abschnitt 14
 * sagt es ausdrücklich.
 *
 * **Klasse C ist nur die Zählung selbst.** Ein negativer oder gebrochener Stand ist kein
 * Regelverstoß, über den sich streiten ließe — §23 zählt Punkte. Deshalb hilft `force` hier
 * nicht.
 */
export function debugSetScore(_context, state, input, now) {
    requireWritable(state);
    const blocking = [];
    if (!knowsPlayer(state, input.player))
        blocking.push('UNKNOWN_OWNER');
    if (!Number.isInteger(input.value) || input.value < 0)
        blocking.push('SCORE_NOT_A_COUNT');
    if (blocking.length > 0) {
        return refuse(...blocking);
    }
    if (scoreOf(state, input.player) === input.value) {
        return { ok: true, state, overridden: [] };
    }
    // Keine Regeleinwendung, die es geben könnte — Nr. 39 lässt jeden Stand zu.
    return settle(setScore(state, input.player, input.value), [], input.force === true, now);
}
/**
 * Die verbleibende Bedenkzeit eines Spielers setzen (§60, „Timer verändern").
 *
 * **Herabsetzen ist Klasse A, Heraufsetzen über das Budget Klasse B** (Nr. 43): jede Restzeit
 * unter `timeBudgetMs` entsteht im Spiel von selbst, einen Wert darüber hat die Partie nie
 * gehabt. Geprüft wird wie bei Nr. 42 der Zustand **danach**, also ist das Zurücknehmen frei.
 *
 * **Die laufende Uhr wird zuerst verbucht.** Sonst rechnete der anschließende Abgleich die
 * Dauer der Debug-Sitzung gegen den **neuen** Wert — der Eingriff wäre um ein paar Sekunden
 * falsch, und zwar unsichtbar. Danach läuft keine Uhr mehr: ein Eingriff hält die Partie an
 * (Nr. 37), und in `PAUSED` steht sie ohnehin (Nr. 26).
 *
 * **Variante A hat keine Uhr** (Nr. 22). Das ist kein Regelverstoß, sondern ein Zustand ohne
 * das Gesuchte — deshalb `NO_CLOCK` als eigene Antwort und nicht Klasse B.
 */
export function debugSetRemaining(_context, state, input, now) {
    requireWritable(state);
    const blocking = [];
    if (!knowsPlayer(state, input.player))
        blocking.push('UNKNOWN_OWNER');
    if (state.timer === undefined)
        blocking.push('NO_CLOCK');
    if (!Number.isInteger(input.remainingMs) || input.remainingMs < 0) {
        blocking.push('TIME_NOT_A_DURATION');
    }
    if (blocking.length > 0) {
        return refuse(...blocking);
    }
    // `NO_CLOCK` oben schließt den undefinierten Fall aus.
    const timer = state.timer;
    const stopped = stopClock(timer, now);
    const remaining = new Map(stopped.remaining);
    remaining.set(input.player, input.remainingMs);
    const rules = input.remainingMs > timer.budgetMs ? [{ kind: 'RULE', code: 'TIME_OVER_BUDGET' }] : [];
    return settle({ ...state, timer: { ...stopped, remaining } }, rules, input.force === true, now);
}
/**
 * Die aktive Person setzen (§60, „aktive Person ändern").
 *
 * **Die Zugreihenfolge bleibt stehen** (Nr. 37): ein Eingriff rückt sie nicht weiter und zählt
 * die Zugnummer nicht hoch. Die Uhr folgt dem Zugrecht von selbst — das Anhalten verbucht die
 * Zeit des alten Aktiven, und beim Fortsetzen startet die Uhr des neuen (`syncClock`, §38).
 *
 * **Ein Ausgeschiedener ist Klasse B** (Nr. 44). §23 verlangt, ihn zu überspringen; wer ihm den
 * Zug gibt, hebt sein Ausscheiden praktisch auf (§22, der Pool bleibt beim Zeitablauf
 * unberührt). **Seine Uhr steht dabei auf 0:00 und startet nicht** — er zieht also ohne Uhr,
 * solange ihm niemand Zeit gibt (`game-clock.ts`, Nr. 43).
 *
 * **Ein Spieler außerhalb der Zugreihenfolge ist Klasse C** — „ein Sektor ohne Spieler" nennt
 * Nr. 38 selbst als Beispiel. Geprüft wird gegen die Zugreihenfolge und nicht gegen die
 * Spielerliste: die Zustandsprüfung verlangt genau das (`game-state.ts`), und ab `PLAYING`
 * enthält die Reihenfolge jeden Mitspieler.
 */
export function debugSetActivePlayer(_context, state, input, now) {
    requireWritable(state);
    if (!state.turnOrder.includes(input.player)) {
        return refuse('UNKNOWN_OWNER');
    }
    if (state.activePlayer === input.player) {
        return { ok: true, state, overridden: [] };
    }
    const rules = isEliminated(state, input.player)
        ? [{ kind: 'RULE', code: 'ELIMINATED_ACTIVE_PLAYER' }]
        : [];
    return settle({ ...state, activePlayer: input.player }, rules, input.force === true, now);
}
//# sourceMappingURL=debug.js.map